import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type FormEvent } from 'react';

import { formatMoney } from '@/shared/lib/format';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { Card, CardBody, CardHeader } from '@/shared/ui/Card';
import type { Column } from '@/shared/ui/DataTable';
import { Field } from '@/shared/ui/Field';
import { Modal } from '@/shared/ui/Modal';
import { Select } from '@/shared/ui/Select';
import { Textarea } from '@/shared/ui/Textarea';
import { useToast } from '@/shared/ui/useToast';

import {
  creditConsumption,
  type ConsultationLimit,
  type ConsultationLimits,
  type ConsultationLimitsCost,
  type LimitPeriod,
  type LimitSubject,
  type LimitSubjectKind,
} from './api';
import {
  CustoDoMes,
  ListaDeLimites,
  PadraoPorCorretor,
  ResumoDaEmpresa,
} from './ConsultationLimitsView';
import { periodosDoLimite, quantidadeDoTexto, rotuloDoEscopo } from './labels';

/** Tempo sem digitar antes da busca: cada busca entra na trilha da plataforma. */
const ESPERA_DA_BUSCA_MS = 300;
const TAMANHO_MINIMO_DA_BUSCA = 3;

function chaveDosLimites(tenantId: string) {
  return ['platform', 'limites-de-consulta', tenantId] as const;
}

/**
 * Limites de consulta ao bureau, editaveis pela administracao da plataforma (ADR 0098).
 *
 * Tres limites pesam sobre uma consulta: o da empresa, o do corretor e o do tomador, e
 * vale o mais restritivo. O da empresa substitui o parametro consulta_limite_mensal, que
 * continua sendo o padrao de quem nao tem limite definido. "Sem limite" e escolha
 * expressa, e fica visivel, porque a consulta real pode ser cobrada.
 *
 * Esconder ou mostrar aqui e orientacao: quem decide e o servidor.
 */
export function ConsultationLimitsSection({
  empresas,
}: {
  empresas: { id: string; name: string }[];
}) {
  const queryClient = useQueryClient();
  const [escolhida, setEscolhida] = useState('');
  const empresa = escolhida || (empresas[0]?.id ?? '');

  const limites = useQuery({
    queryKey: chaveDosLimites(empresa),
    queryFn: ({ signal }) => creditConsumption.platformLimits(empresa, signal),
    enabled: empresa !== '',
  });

  const [editarEmpresa, setEditarEmpresa] = useState(false);
  const [voltarAoPadrao, setVoltarAoPadrao] = useState(false);
  const [editarPadrao, setEditarPadrao] = useState(false);
  const [limparPadrao, setLimparPadrao] = useState(false);
  const [incluir, setIncluir] = useState(false);
  const [alterar, setAlterar] = useState<ConsultationLimit | null>(null);
  const [remover, setRemover] = useState<ConsultationLimit | null>(null);

  const atualizar = (dados: ConsultationLimits) =>
    queryClient.setQueryData(chaveDosLimites(empresa), dados);

  const acoesDaLinha: Column<ConsultationLimit> = {
    key: 'acao',
    header: 'Acao',
    align: 'right',
    render: (limite) => (
      <div className="flex justify-end gap-2">
        <Button
          size="sm"
          variant="secondary"
          aria-label={`Alterar o limite de ${limite.subject_label}`}
          onClick={() => setAlterar(limite)}
        >
          Alterar
        </Button>
        <Button
          size="sm"
          variant="secondary"
          aria-label={`Remover o limite de ${limite.subject_label}`}
          onClick={() => setRemover(limite)}
        >
          Remover
        </Button>
      </div>
    ),
  };

  const dados = limites.data;

  return (
    <Card>
      <CardHeader
        title="Limites de consulta"
        description="Quantas consultas ao bureau cada empresa, corretor e tomador pode fazer. Vale o limite mais restritivo, e a consulta valida reaproveitada nao conta."
      />
      <CardBody className="flex flex-col gap-4">
        <div className="max-w-sm">
          <Select
            label="Empresa dos limites"
            value={empresa}
            options={empresas.map((e) => ({ value: e.id, label: e.name }))}
            onChange={(evento) => setEscolhida(evento.target.value)}
          />
        </div>

        {limites.isError ? (
          <Alert tone="danger" title="Nao foi possivel carregar os limites">
            {limites.error.message}
          </Alert>
        ) : null}

        {dados ? (
          <>
            <ResumoDaEmpresa
              limite={dados.company}
              acoes={
                <>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => setEditarEmpresa(true)}
                  >
                    Alterar limite da empresa
                  </Button>
                  {dados.company.source === 'plataforma' ? (
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => setVoltarAoPadrao(true)}
                    >
                      Voltar ao padrao
                    </Button>
                  ) : null}
                </>
              }
            />
            {dados.cost ? <CustoDoMes custo={dados.cost} /> : null}
            <PadraoPorCorretor
              padrao={dados.broker_default}
              acoes={
                <>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => setEditarPadrao(true)}
                  >
                    Alterar padrao por corretor
                  </Button>
                  {dados.broker_default.source === 'plataforma' ? (
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => setLimparPadrao(true)}
                    >
                      Voltar a 10 por mes
                    </Button>
                  ) : null}
                </>
              }
            />

            <div className="flex flex-wrap items-center justify-between gap-2">
              <h4 className="text-sm font-semibold text-ink-900">
                Limites por corretor e por tomador
              </h4>
              <Button size="sm" onClick={() => setIncluir(true)}>
                Incluir limite
              </Button>
            </div>
            <ListaDeLimites
              itens={dados.items}
              carregando={false}
              colunasExtras={[acoesDaLinha]}
            />
          </>
        ) : null}
      </CardBody>

      {editarEmpresa && dados ? (
        <CompanyLimitModal
          tenantId={empresa}
          atual={dados.company}
          custo={dados.cost}
          onClose={() => setEditarEmpresa(false)}
          onDone={(novos) => {
            setEditarEmpresa(false);
            atualizar(novos);
          }}
        />
      ) : null}
      {voltarAoPadrao ? (
        <RemovalModal
          titulo="Voltar ao padrao"
          descricao="A empresa volta ao limite dos parametros do motor de credito."
          confirmar="Voltar ao padrao"
          remover={(motivo) => creditConsumption.clearCompanyLimit(empresa, motivo)}
          onClose={() => setVoltarAoPadrao(false)}
          onDone={(novos) => {
            setVoltarAoPadrao(false);
            atualizar(novos);
          }}
        />
      ) : null}
      {editarPadrao && dados ? (
        <BrokerDefaultLimitModal
          tenantId={empresa}
          atual={dados.broker_default.limit}
          onClose={() => setEditarPadrao(false)}
          onDone={(novos) => {
            setEditarPadrao(false);
            atualizar(novos);
          }}
        />
      ) : null}
      {limparPadrao ? (
        <RemovalModal
          titulo="Voltar ao padrao por corretor"
          descricao="A corretora sem limite proprio volta a 10 consultas por mes."
          confirmar="Voltar a 10 por mes"
          remover={(motivo) =>
            creditConsumption.clearBrokerDefaultLimit(empresa, motivo)
          }
          onClose={() => setLimparPadrao(false)}
          onDone={(novos) => {
            setLimparPadrao(false);
            atualizar(novos);
          }}
        />
      ) : null}
      {incluir ? (
        <SubjectLimitModal
          tenantId={empresa}
          atual={null}
          onClose={() => setIncluir(false)}
          onDone={(novos) => {
            setIncluir(false);
            atualizar(novos);
          }}
        />
      ) : null}
      {alterar ? (
        <SubjectLimitModal
          tenantId={empresa}
          atual={alterar}
          onClose={() => setAlterar(null)}
          onDone={(novos) => {
            setAlterar(null);
            atualizar(novos);
          }}
        />
      ) : null}
      {remover ? (
        <RemovalModal
          titulo={`Remover o limite de ${remover.subject_label}`}
          descricao={`${rotuloDoEscopo[remover.scope]} sem limite proprio: as consultas continuam pesando no limite da empresa.`}
          confirmar="Remover limite"
          remover={(motivo) =>
            remover.scope === 'corretor'
              ? creditConsumption.removeBrokerLimit(empresa, remover.subject_id, motivo)
              : creditConsumption.removePolicyholderLimit(
                  empresa,
                  remover.subject_id,
                  motivo,
                )
          }
          onClose={() => setRemover(null)}
          onDone={(novos) => {
            setRemover(null);
            atualizar(novos);
          }}
        />
      ) : null}
    </Card>
  );
}

function ErroDoServidor({ erro }: { erro: Error | null }) {
  if (!erro) return null;
  return (
    <Alert tone="danger" title="Nao foi possivel salvar">
      {erro.message}
    </Alert>
  );
}

function CampoDeJustificativa({
  valor,
  onChange,
}: {
  valor: string;
  onChange: (valor: string) => void;
}) {
  return (
    <Textarea
      label="Justificativa (opcional)"
      hint="Fica na trilha da plataforma."
      maxLength={2000}
      value={valor}
      onChange={(evento) => onChange(evento.target.value)}
    />
  );
}

function CompanyLimitModal({
  tenantId,
  atual,
  custo,
  onClose,
  onDone,
}: {
  tenantId: string;
  atual: ConsultationLimit;
  custo: ConsultationLimitsCost | undefined;
  onClose: () => void;
  onDone: (dados: ConsultationLimits) => void;
}) {
  const toast = useToast();
  const [semLimite, setSemLimite] = useState(atual.unlimited);
  const [ciente, setCiente] = useState(false);
  const [quantidade, setQuantidade] = useState(
    atual.limit === null ? '' : String(atual.limit),
  );
  const [justificativa, setJustificativa] = useState('');

  const numero = quantidadeDoTexto(quantidade);
  const erroDaQuantidade =
    !semLimite && quantidade.trim() !== '' && numero === null
      ? 'Informe um numero inteiro de 0 a 1.000.000.'
      : undefined;

  const salvar = useMutation({
    mutationFn: () =>
      creditConsumption.setCompanyLimit(
        tenantId,
        semLimite
          ? { unlimited: true, reason: justificativa }
          : { max_queries: numero ?? 0, reason: justificativa },
      ),
    onSuccess: (dados) => {
      toast.success('Limite da empresa salvo.');
      onDone(dados);
    },
  });

  const pronto = semLimite ? ciente : numero !== null;

  const enviar = (evento: FormEvent) => {
    evento.preventDefault();
    if (pronto) salvar.mutate();
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="Limite mensal da empresa"
      description="Substitui o limite dos parametros do motor de credito. Vale a partir da proxima consulta."
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={salvar.isPending}>
            Cancelar
          </Button>
          <Button
            type="submit"
            form="limite-da-empresa"
            loading={salvar.isPending}
            disabled={!pronto}
          >
            Salvar limite
          </Button>
        </>
      }
    >
      <form
        id="limite-da-empresa"
        onSubmit={enviar}
        className="flex flex-col gap-3"
        aria-label="Limite da empresa"
      >
        <ErroDoServidor erro={salvar.error} />
        <fieldset className="flex flex-col gap-2 text-sm text-ink-700">
          <legend className="sr-only">Tipo do limite</legend>
          <label className="flex items-center gap-2">
            <input
              type="radio"
              name="tipo-do-limite"
              checked={!semLimite}
              onChange={() => setSemLimite(false)}
            />
            Com limite mensal
          </label>
          <label className="flex items-center gap-2">
            <input
              type="radio"
              name="tipo-do-limite"
              checked={semLimite}
              onChange={() => {
                setSemLimite(true);
                setCiente(false);
              }}
            />
            Sem limite
          </label>
        </fieldset>
        {semLimite ? (
          <Alert tone="warning" title="A consulta real pode ser cobrada">
            <p>
              Sem limite, nenhuma quantidade barra as consultas desta empresa. Os
              limites de corretor e de tomador e o teto financeiro dos parametros
              continuam valendo.
            </p>
            {custo ? (
              <p className="mt-1">
                Custo do mes ate agora: {formatMoney(custo.month_spent)}.
              </p>
            ) : null}
            <label className="mt-2 flex items-center gap-2 text-xs">
              <input
                type="checkbox"
                checked={ciente}
                onChange={(evento) => setCiente(evento.target.checked)}
              />
              Entendo que o custo do mes fica sem teto de quantidade
            </label>
          </Alert>
        ) : (
          <Field
            label="Consultas por mes"
            inputMode="numeric"
            required
            value={quantidade}
            onChange={(evento) => setQuantidade(evento.target.value)}
            error={erroDaQuantidade}
          />
        )}
        <CampoDeJustificativa valor={justificativa} onChange={setJustificativa} />
      </form>
    </Modal>
  );
}

function BrokerDefaultLimitModal({
  tenantId,
  atual,
  onClose,
  onDone,
}: {
  tenantId: string;
  atual: number;
  onClose: () => void;
  onDone: (dados: ConsultationLimits) => void;
}) {
  const toast = useToast();
  const [quantidade, setQuantidade] = useState(String(atual));
  const [justificativa, setJustificativa] = useState('');
  const numero = quantidadeDoTexto(quantidade);
  const erroDaQuantidade =
    quantidade.trim() !== '' && numero === null
      ? 'Informe um numero inteiro de 0 a 1.000.000.'
      : undefined;

  const salvar = useMutation({
    mutationFn: () =>
      creditConsumption.setBrokerDefaultLimit(tenantId, {
        max_queries: numero ?? 0,
        reason: justificativa,
      }),
    onSuccess: (dados) => {
      toast.success('Padrao por corretor salvo.');
      onDone(dados);
    },
  });

  const enviar = (evento: FormEvent) => {
    evento.preventDefault();
    if (numero !== null) salvar.mutate();
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="Padrao por corretor"
      description="Vale para a corretora sem limite proprio, nas consultas pedidas por ela, a partir da proxima consulta."
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={salvar.isPending}>
            Cancelar
          </Button>
          <Button
            type="submit"
            form="padrao-por-corretor"
            loading={salvar.isPending}
            disabled={numero === null}
          >
            Salvar limite
          </Button>
        </>
      }
    >
      <form
        id="padrao-por-corretor"
        onSubmit={enviar}
        className="flex flex-col gap-3"
        aria-label="Padrao por corretor"
      >
        <ErroDoServidor erro={salvar.error} />
        <Field
          label="Consultas por mes"
          inputMode="numeric"
          required
          value={quantidade}
          onChange={(evento) => setQuantidade(evento.target.value)}
          error={erroDaQuantidade}
        />
        <CampoDeJustificativa valor={justificativa} onChange={setJustificativa} />
      </form>
    </Modal>
  );
}

/** Busca de corretora ou de tomador pelo nome ou pelo documento. */
function BuscaDeSujeito({
  tenantId,
  tipo,
  escolhido,
  onEscolher,
}: {
  tenantId: string;
  tipo: LimitSubjectKind;
  escolhido: LimitSubject | null;
  onEscolher: (sujeito: LimitSubject | null) => void;
}) {
  const [termo, setTermo] = useState('');
  const [procurado, setProcurado] = useState('');

  useEffect(() => {
    const espera = setTimeout(() => setProcurado(termo.trim()), ESPERA_DA_BUSCA_MS);
    return () => clearTimeout(espera);
  }, [termo]);

  const busca = useQuery({
    queryKey: ['platform', 'limites-de-consulta', tenantId, 'busca', tipo, procurado],
    queryFn: ({ signal }) =>
      creditConsumption.searchLimitSubjects(tenantId, tipo, procurado, signal),
    enabled: procurado.length >= TAMANHO_MINIMO_DA_BUSCA,
  });

  if (escolhido) {
    return (
      <div className="flex items-center justify-between gap-2 rounded-md border border-line p-2 text-sm">
        <div className="min-w-0">
          <p className="truncate font-medium text-ink-900">{escolhido.label}</p>
          <p className="truncate text-2xs text-ink-500">{escolhido.document}</p>
        </div>
        <Button size="sm" variant="secondary" onClick={() => onEscolher(null)}>
          Trocar
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <Field
        label="Buscar pelo nome ou documento"
        hint="Digite pelo menos 3 caracteres. O CPF aparece mascarado."
        value={termo}
        onChange={(evento) => setTermo(evento.target.value)}
      />
      {busca.isError ? (
        <Alert tone="danger" title="Nao foi possivel buscar">
          {busca.error.message}
        </Alert>
      ) : null}
      {busca.data?.length === 0 ? (
        <p className="text-xs text-ink-500">Nada encontrado nesta empresa.</p>
      ) : null}
      {busca.data && busca.data.length > 0 ? (
        <ul className="flex max-h-48 flex-col gap-1 overflow-y-auto">
          {busca.data.map((sujeito) => (
            <li key={sujeito.id}>
              <button
                type="button"
                className="w-full rounded-md border border-line p-2 text-left text-sm hover:bg-ink-50"
                onClick={() => onEscolher(sujeito)}
              >
                <span className="block truncate text-ink-900">{sujeito.label}</span>
                <span className="block truncate text-2xs text-ink-500">
                  {sujeito.document}
                </span>
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function SubjectLimitModal({
  tenantId,
  atual,
  onClose,
  onDone,
}: {
  tenantId: string;
  atual: ConsultationLimit | null;
  onClose: () => void;
  onDone: (dados: ConsultationLimits) => void;
}) {
  const toast = useToast();
  const [tipo, setTipo] = useState<LimitSubjectKind>(
    atual?.scope === 'tomador' ? 'tomador' : 'corretor',
  );
  const [sujeito, setSujeito] = useState<LimitSubject | null>(
    atual
      ? {
          id: atual.subject_id,
          label: atual.subject_label,
          document: atual.subject_document,
        }
      : null,
  );
  const [quantidade, setQuantidade] = useState(
    atual?.limit === null || atual === null ? '' : String(atual.limit),
  );
  const [periodo, setPeriodo] = useState<LimitPeriod>(atual?.period ?? 'mensal');
  const [barrar, setBarrar] = useState(atual?.auto_block ?? true);
  const [justificativa, setJustificativa] = useState('');

  const numero = quantidadeDoTexto(quantidade);
  const erroDaQuantidade =
    quantidade.trim() !== '' && numero === null
      ? 'Informe um numero inteiro de 0 a 1.000.000.'
      : undefined;
  const pronto = sujeito !== null && numero !== null;

  const salvar = useMutation({
    mutationFn: () => {
      const id = sujeito?.id ?? '';
      const maximo = numero ?? 0;
      return tipo === 'corretor'
        ? creditConsumption.setBrokerLimit(tenantId, id, {
            max_queries: maximo,
            period: periodo,
            auto_block: barrar,
            reason: justificativa,
          })
        : creditConsumption.setPolicyholderLimit(tenantId, id, {
            max_queries: maximo,
            reason: justificativa,
          });
    },
    onSuccess: (dados) => {
      toast.success('Limite salvo.');
      onDone(dados);
    },
  });

  const enviar = (evento: FormEvent) => {
    evento.preventDefault();
    if (pronto) salvar.mutate();
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={
        atual
          ? `Alterar o limite de ${atual.subject_label}`
          : 'Incluir limite de consulta'
      }
      description="Vale a partir da proxima consulta. A consulta valida reaproveitada nao conta."
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={salvar.isPending}>
            Cancelar
          </Button>
          <Button
            type="submit"
            form="limite-do-sujeito"
            loading={salvar.isPending}
            disabled={!pronto}
          >
            Salvar limite
          </Button>
        </>
      }
    >
      <form
        id="limite-do-sujeito"
        onSubmit={enviar}
        className="flex flex-col gap-3"
        aria-label="Limite de corretor ou tomador"
      >
        <ErroDoServidor erro={salvar.error} />
        {atual ? null : (
          <Select
            label="Limite de"
            value={tipo}
            options={[
              { value: 'corretor', label: 'Corretor' },
              { value: 'tomador', label: 'Tomador' },
            ]}
            onChange={(evento) => {
              setTipo(evento.target.value as LimitSubjectKind);
              setSujeito(null);
            }}
          />
        )}
        {atual ? null : (
          <BuscaDeSujeito
            tenantId={tenantId}
            tipo={tipo}
            escolhido={sujeito}
            onEscolher={setSujeito}
          />
        )}
        <Field
          label={tipo === 'corretor' ? 'Quantidade de consultas' : 'Consultas por mes'}
          inputMode="numeric"
          required
          value={quantidade}
          onChange={(evento) => setQuantidade(evento.target.value)}
          error={erroDaQuantidade}
        />
        {tipo === 'corretor' ? (
          <>
            <Select
              label="Periodo do limite"
              value={periodo}
              options={periodosDoLimite}
              onChange={(evento) => setPeriodo(evento.target.value as LimitPeriod)}
            />
            <label className="flex items-center gap-2 text-xs text-ink-700">
              <input
                type="checkbox"
                checked={barrar}
                onChange={(evento) => setBarrar(evento.target.checked)}
              />
              Barrar a consulta ao atingir o limite
            </label>
            {barrar ? null : (
              <p className="text-2xs text-ink-500">
                Desligado, o limite so informa: nenhuma consulta e barrada por ele.
              </p>
            )}
          </>
        ) : null}
        <CampoDeJustificativa valor={justificativa} onChange={setJustificativa} />
      </form>
    </Modal>
  );
}

function RemovalModal({
  titulo,
  descricao,
  confirmar,
  remover,
  onClose,
  onDone,
}: {
  titulo: string;
  descricao: string;
  confirmar: string;
  remover: (motivo: string) => Promise<ConsultationLimits>;
  onClose: () => void;
  onDone: (dados: ConsultationLimits) => void;
}) {
  const toast = useToast();
  const [justificativa, setJustificativa] = useState('');
  const excluir = useMutation({
    mutationFn: () => remover(justificativa),
    onSuccess: (dados) => {
      toast.success('Limite removido.');
      onDone(dados);
    },
  });

  return (
    <Modal
      open
      onClose={onClose}
      title={titulo}
      description={descricao}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={excluir.isPending}>
            Cancelar
          </Button>
          <Button
            variant="danger"
            loading={excluir.isPending}
            onClick={() => excluir.mutate()}
          >
            {confirmar}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <ErroDoServidor erro={excluir.error} />
        <CampoDeJustificativa valor={justificativa} onChange={setJustificativa} />
      </div>
    </Modal>
  );
}
